import { mapGetters } from 'vuex'
import { clone } from 'ramda'
import IconPickerComponent from '@/components/commons/icon-picker/IconPickerComponent.vue'
import { enterpriseService } from '@/services/enterprise.service'
import { removeEmptyFields } from '@/tools/functions'
import AutocompleteComponent from "@/components/commons/autocomplete/AutocompleteComponent.vue";

export default {
    name: 'EnterpriseHeaderTabComponent',
    components: {
        IconPickerComponent,
        AutocompleteComponent
    },
    model: {
        prop: 'model',
        event: 'change'
    },
    props: {
        model: {
            type: Object,
            required: true
        },
        validationRules: {
            type: Object,
            required: true
        },
        validForm: {
            type: Boolean,
            required: true
        },
        resetFormValidation: {
            type: Boolean,
            required: true
        },
        update: {
            type: Boolean,
            required: true
        }
    },
    data: () => ({
        // model para el formulario
        isValidForm: false,
        // logo seleccionado
        logoSelected: null,
        // logo errors
        logoErrors: [],
        // para mostrar el formulario de edición del logo
        showLogoUploadForm: true,
        // para mostrar el formulario de edición de los menú
        showMenuEditionForm: false,
        // para mostrar el formulario de edición de los sub-menú
        showSubMenuEditionForm: false,
        // modelo de datos para la edición de un menú
        currentMenuEdit: {
            id: null,
            title: null,
            translations: {
                es: null,
                en: null,
                pt: null
            },
            subMenu: []
        },
        // modelo de datos para la edición de un submenú
        currentSubMenuEdit: {
            id: null,
            title: null,
            icon: 'mdi-view-compact',
            route: null,
            key: null,
            keys: [],
            translations: {
                es: null,
                en: null,
                pt: null
            },
            menuId: null,
            menuTitle: null
        },
        // indica si el formulario se abrió para actualizar datos (true) o crear (false)
        menuFormsUpdate: false,
        // validación de los formularios de los menú
        menuValidForm: false,
        subMenuValidForm: false,
        // model del icon-picker
        iconPickerModel: {
            icon: 'mdi-view-compact'
        },
        // rutas de los componentes y privilegios para el selector de las opciones de sub-menú
        routesPrivileges: [],
        // loading de inicialización
        initLoading: false,
        // componente seleccionado en el selector de componentes
        componentSelected: null,
        // errores para los textfields de locales
        esTextfieldErrors: [],
        enTextfieldErrors: [],
        ptTextfieldErrors: [],
        // error del componente seleccionado
        componentSelectedErrors: []
    }),
    computed: {
        /**
         * Src del logo
         */
        logoSource() {
            if (this.model.logo) {
                return 'data:image/png;base64,' + this.model.logo
            } else {
                return require('../../../../../assets/logo.png')
            }
        },
        /**
         * getter del username
         */
        ...mapGetters('user', [
            'getUsername',
            'getLocale'
        ]),
        /**
         * getter de tema dark
         */
        ...mapGetters('app', [
            'getDark'
        ])
    },
    created () {
    },
    async mounted () {
        // cargo el selector de rutas (componentes)
        await this.initialize()
    },
    methods: {
        /**
         * Cuando selecciona la imágen, convierte a BASE64 y la asigna al model
         * @param {*} files
         */
        onFileSelected (file) {
            if (file) {
                // base 64 (para guardar en el modelo)
                const binaryReader = new FileReader()
                binaryReader.readAsBinaryString(file)
                binaryReader.onload = () => {
                    this.model.logo = btoa(binaryReader.result)
                }
                binaryReader.onerror = () => {
                    console.error('Error al leer la imagen subida');
                }

                // image (para validar la relación de aspecto)
                const that = this
                const imageReader = new FileReader()
                imageReader.readAsDataURL(file)
                imageReader.onload = () => {
                    const image = new Image()
                    image.src = imageReader.result
                    image.onload = function() {
                        const aspectRatio = image.height > 0 ? image.width / image.height : 0
                        if (aspectRatio < 2.5) {
                            that.logoErrors.push(that.$t('enterprise.error.invalidAspectRatio'))
                        } else {
                            that.logoErrors = []
                        }
                    }
                }
                imageReader.onerror = () => {
                    console.error('Error al leer la imagen subida');
                }
            } else {
                this.model.logo = null
                this.logoErrors = []
            }
        },
        /**
         * Elimina el logo de la emrpesa
         */
        deleteLogo () {
            this.model.logo = null
            this.logoSelected = null
            this.logoErrors = []
        },
        /**
         * Realiza la traducción son las claves configuradas por el usuario
         * TO DO: a partir de la clave, buscar el valor en el json de traducciones
         * @param {*} key
         */
        translate (key) {
            if (key) {
                const splittedKey = key.split('.')
                let result = this.model.locales[this.getLocale]
                if (result) {
                    splittedKey.forEach(keyPart => {
                        if (result) {
                            result = result[keyPart]
                        } else {
                            result = key
                        }
                    })
                    return result == null ? key : result
                } else {
                    // si no existe la traducción para la clave, devuelvo la clave
                    return key
                }
            } else {
                return ''
            }
        },
        /**
         * Devuelve las tres traducciones configuradas para una clave
         * Se utiliza cuando se cargan los formularios para edición de los menús y sub-menús
         * @param {*} key
         */
        getTranslations (key) {
            const splittedKey = key.split('.')
            const result = {}
            let aux
            // obtengo las claves del primer nivel del json de traducciones (corresponden a los lenguajes)
            const translationLanguages = Object.keys(this.model.locales)
            translationLanguages.forEach(lang => {
                aux = this.model.locales[lang]
                splittedKey.forEach(keyPart => {
                    if (aux[keyPart]) {
                        aux = aux[keyPart]
                    } else {
                        aux = key
                    }
                })
                result[lang] = clone(aux)
                aux = null
            })
            return result
        },
        /**
         * Setea nuevas traducciones para una clave del json
         * @param {*} key
         * @param {*} translations
         */
        setTranslations (key, translations) {
            const splittedKey = key.split('.')
            let aux
            // obtengo las claves del primer nivel del json de traducciones (corresponden a los lenguajes)
            const translationLanguages = Object.keys(this.model.locales)
            translationLanguages.forEach(lang => {
                aux = this.model.locales[lang]
                for (let i = 0; i < (splittedKey.length - 1); i++) {
                    if (aux[splittedKey[i]]) {
                        aux = aux[splittedKey[i]]
                    } else {
                        aux[splittedKey[i]] = {}
                        aux = aux[splittedKey[i]]
                    }
                }
                aux[splittedKey[splittedKey.length - 1]] = translations[lang]
            })
        },
        /**
         * Eliminar las traducciones para una clave
         * @param {*} key
         */
        deleteTranslations (key) {
            const splittedKey = key.split('.')
            let aux
            // obtengo las claves del primer nivel del json de traducciones (corresponden a los lenguajes)
            const translationLanguages = Object.keys(this.model.locales)
            translationLanguages.forEach(lang => {
                aux = this.model.locales[lang]
                for (let i = 0; i < (splittedKey.length - 1); i++) {
                    if (aux[splittedKey[i]]) {
                        aux = aux[splittedKey[i]]
                    } else {
                        aux[splittedKey[i]] = {}
                        aux = aux[splittedKey[i]]
                    }
                }
                delete aux[splittedKey[splittedKey.length - 1]]
            })
        },
        /**
         * Purgar locales
         */
        purgeLocales () {
            const translationLanguages = Object.keys(this.model.locales)
            translationLanguages.forEach(lang => {
                // purgar los locales (eliminar claves que tienen un contenido vacío)
                removeEmptyFields(this.model.locales[lang])
            })
        },
        /**
         * Cuando se hace click en el overlay sobre el logo, activa el formulario de edición del logo
         */
        editLogo () {
            this.resetMenuForm()
            this.resetSubMenuForm()
            this.showMenuEditionForm = false
            this.showSubMenuEditionForm = false
            this.showLogoUploadForm = true
        },
        /**
         * Para editar un menú
         * @param {*} menu
         */
        editMenu (menu) {
            this.menuFormsUpdate = true
            // reset formulario
            this.resetMenuForm()

            // cargo el modelo de datos
            this.currentMenuEdit.id = menu.id
            this.currentMenuEdit.title = menu.title
            this.currentMenuEdit.translations = this.getTranslations(menu.title)
            this.currentMenuEdit.subMenu = menu.subMenu

            // muestro el formulario
            this.showLogoUploadForm = false
            this.showSubMenuEditionForm = false
            this.showMenuEditionForm = true
        },
        /**
         * Eliminar un menú
         * @param {*} index
         * @param {*} menu
         */
        deleteMenu (index, menu) {
            // elimino las traducciones de los sub-menú
            menu.subMenu.forEach(element => {
                this.deleteTranslations(element.title)
            })
            // elimino las traducciones del menú
            this.deleteTranslations(menu.title)
            // purgo los locales
            this.purgeLocales()
            // elimino el menu
            this.model.menu.splice(index, 1)
            this.cancelMenu()
            this.cancelSubMenu()
        },
        /**
         * Para editar un sub-menú
         * @param {*} subMenu
         * @param {*} menu
         */
        editSubMenu (subMenu, menu) {
            this.menuFormsUpdate = true
            // reset formulario
            this.resetSubMenuForm()

            // cargo el modelo de datos
            this.currentSubMenuEdit.id = subMenu.id
            this.currentSubMenuEdit.title = subMenu.title
            this.currentSubMenuEdit.icon = subMenu.icon
            this.iconPickerModel.icon = subMenu.icon ? subMenu.icon : 'mdi-view-compact'
            this.currentSubMenuEdit.key = subMenu.key
            this.currentSubMenuEdit.route = subMenu.route
            this.currentSubMenuEdit.keys = subMenu.keys
            this.currentSubMenuEdit.translations = this.getTranslations(subMenu.title)
            this.currentSubMenuEdit.menuId = menu.id
            this.currentSubMenuEdit.menuTitle = menu.title

            // componente de la ruta
            this.componentSelected = this.routesPrivileges.find(element => element.route === subMenu.route)

            // muestro el formulario
            this.showLogoUploadForm = false
            this.showMenuEditionForm = false
            this.showSubMenuEditionForm = true
        },
        /**
         * Eliminar una opción de un menú
         * @param {*} menu
         * @param {*} index
         */
        deleteSubMenu (menu, index) {
            // elimino la traducción
            this.deleteTranslations(menu.subMenu[index].title)
            // purgo los locales
            this.purgeLocales()
            menu.subMenu.splice(index, 1);
            this.cancelSubMenu()
        },
        /**
         * Restablece el formulario de edición de un menú
         */
        resetMenuForm () {
            this.currentMenuEdit = {
                id: null,
                title: null,
                translations: {
                    es: null,
                    en: null,
                    pt: null
                },
                subMenu: []
            }
            if (this.$refs.menuValidForm) {
                this.$refs.menuValidForm.resetValidation()
            }
        },
        /**
         * Restablece el formulario de edición de un sub-menú
         */
        resetSubMenuForm () {
            this.currentSubMenuEdit = {
                id: null,
                title: null,
                icon: 'mdi-view-compact',
                key: null,
                route: null,
                keys: [],
                translations: {
                    es: null,
                    en: null,
                    pt: null
                },
                menuId: null,
                menuTitle: null
            }
            this.iconPickerModel.icon = 'mdi-view-compact'
            this.componentSelected = null
            if (this.$refs.subMenuValidForm) {
                this.$refs.subMenuValidForm.resetValidation()
            }
        },
        /**
         * Agregar un nuevo menú
         */
        addMenu () {
            this.menuFormsUpdate = false
            // reset formulario
            this.resetMenuForm()

            // muestro el formulario
            this.showLogoUploadForm = false
            this.showSubMenuEditionForm = false
            this.showMenuEditionForm = true
        },
        /**
         * Agregar una opción al menú (habilita el formulario)
         * @param {*} menu
         */
        addMenuOption(menu) {
            this.menuFormsUpdate = false
            // reset formulario
            this.resetSubMenuForm()

            // id del menú
            this.currentSubMenuEdit.menuId = menu.id
            this.currentSubMenuEdit.menuTitle = menu.title

            // muestro el formulario
            this.showLogoUploadForm = false
            this.showMenuEditionForm = false
            this.showSubMenuEditionForm = true
        },
        /**
         * Crea / actualiza un menú
         */
        saveMenu () {
            if (this.menuFormsUpdate) {
                // UPDATE
                // lo único que puede actualizarse son las traducciones
                this.setTranslations(this.currentMenuEdit.title, this.currentMenuEdit.translations)
            } else {
                // CREATE
                // La clave de la traducción comienza con el id, que está compuesto por la palabra menu#{número de menú}
                let id
                if (this.model.menu.length > 0) {
                    const lastId = (this.model.menu[this.model.menu.length - 1]).id
                    const lastIdSplitted = lastId.split('#')
                    id = `menu#${Number(lastIdSplitted[1]) + 1}`
                } else {
                    id = 'menu#0'
                }
                
                const newMenu = {
                    id,
                    title: `${id}.title`,
                    subMenu: []
                }
                this.model.menu.push(newMenu)
                this.setTranslations(newMenu.title, this.currentMenuEdit.translations)
            }
            this.cancelMenu()
        },
        /**
         * Cancela la creación / actualización de un menú
         */
        cancelMenu () {
            this.menuFormsUpdate = false
            // reset formulario
            this.resetMenuForm()

            // vuelvo a la vista del file-upload del logo
            this.showMenuEditionForm = false
            this.showSubMenuEditionForm = false
            this.showLogoUploadForm = true
        },
        /**
         * Crea / actualiza una opción de menú
         */
        saveSubMenu () {
            const menu = this.model.menu.find(element => element.id === this.currentSubMenuEdit.menuId)
            if (this.menuFormsUpdate) {
                // UPDATE
                const subMenu = menu.subMenu.find(element => element.id === this.currentSubMenuEdit.id)
                this.setTranslations(this.currentSubMenuEdit.title, this.currentSubMenuEdit.translations)
                subMenu.icon = this.iconPickerModel.icon
                subMenu.key = this.currentSubMenuEdit.key
                subMenu.route = this.currentSubMenuEdit.route
                subMenu.keys = this.currentSubMenuEdit.keys
            } else {
                // CREATE
                /*
                    La clave de la traducción comienza con el id del menu, que está compuesto por la palabra menu#{número de menú}
                    Seguido por la clave subMenu y finalmente subMenu#{número de submenú}
                    Ejemplo: menu#1.subMenu.subMenu#1
                */
                let id
                if (menu.subMenu.length > 0) {
                    const lastId = (menu.subMenu[menu.subMenu.length - 1]).id
                    const lastIdSplitted = lastId.split('#')
                    id = `subMenu#${Number(lastIdSplitted[1]) + 1}`
                } else {
                    id = 'subMenu#0'
                }
                const newSubMenu = {
                    id,
                    title: `${menu.id}.subMenu.${id}`,
                    icon: this.iconPickerModel.icon,
                    key: this.currentSubMenuEdit.key,
                    route: this.currentSubMenuEdit.route,
                    keys: this.currentSubMenuEdit.keys,
                }
                menu.subMenu.push(newSubMenu)
                this.setTranslations(newSubMenu.title, this.currentSubMenuEdit.translations)
            }
            this.cancelSubMenu()
        },
        /**
         * Cancela la creación / actualización de una opción de menú
         */
        cancelSubMenu () {
            this.menuFormsUpdate = false
            // reset formulario
            this.resetSubMenuForm()

            // vuelvo a la vista del file-upload del logo
            this.showSubMenuEditionForm = false
            this.showMenuEditionForm = false
            this.showLogoUploadForm = true
        },
        /**
         * Obtener los datos necesarios para el ABM
         */
        async initialize () {
            this.initLoading = true
            this.routesPrivileges = await enterpriseService.getRoutesPrivileges()
            this.initLoading = false
        },
        /**
         * Valida que la traducción de un menú/submenú no se repita para otros menús/submenús ya existentes
         * @param {*} title
         * @param {*} locale
         * @param {*} elements
         * @param {*} id
         * @param {*} isFormMenu
         */
        validateTranslation (title, locale, elements, id, isFormMenu = true) {
            let result = true
            let translations
            let i = 0
            let element
            while (i < elements.length && result) {
                element = elements[i]
                // si el id tiene valor, es un update
                if (id != null) {
                    if (element.id !== id) {
                        translations = this.getTranslations(element.title)
                        if (translations[locale]) {
                            result = result && translations[locale] !== title
                        }
                    }
                } else {
                    translations = this.getTranslations(element.title)
                    if (translations[locale]) {
                        result = result && translations[locale] !== title
                    }
                }
                i++
            }

            // mensajes de error
            switch (locale) {
                case 'es':
                    if (result) {
                        this.esTextfieldErrors = []
                    } else {
                        this.esTextfieldErrors.push(this.$t(`enterprise.editDialog.headerLogo.translations.repeated${isFormMenu ? 'Menu' : 'Submenu'}`))
                    }
                    break
                case 'en':
                    if (result) {
                        this.enTextfieldErrors = []
                    } else {
                        this.enTextfieldErrors.push(this.$t(`enterprise.editDialog.headerLogo.translations.repeated${isFormMenu ? 'Menu' : 'Submenu'}`))
                    }
                    break
                case 'pt':
                    if (result) {
                        this.ptTextfieldErrors = []
                    } else {
                        this.ptTextfieldErrors.push(this.$t(`enterprise.editDialog.headerLogo.translations.repeated${isFormMenu ? 'Menu' : 'Submenu'}`))
                    }
                    break
                default:
                    console.error('Locale no programado')
            }
        },
        /**
         * Obtener el arreglo de subMenus de un menu a partir de su id
         * @param {*} menuId
         */
        getSubmenus (menuId) {
            const menu = this.model.menu.find(element => element.id === menuId)
            if (menu) {
                return menu.subMenu
            } else {
                return []
            }
        },
        /**
         * Valida que el componente seleccionado no esté repetido para otra opción
         * @param {*} id
         */
        validateComponentSelected (id) {
            const menu = this.model.menu.find(element => element.id === this.currentSubMenuEdit.menuId)
            let i = 0
            let element
            let result = true
            while (i < menu.subMenu.length && result) {
                element = menu.subMenu[i]
                if (id) {
                    if (element.id !== id && element.key) {
                        result = result && element.key !== this.componentSelected.key
                    }
                } else {
                    if (element.key) {
                        result = result && element.key !== this.componentSelected.key
                    }
                }
                
                i++
            }

            if (result) {
                this.componentSelectedErrors = []
            } else {
                this.componentSelectedErrors.push(this.$t('enterprise.editDialog.headerLogo.componentSelectedRepeated'))
            }
        },
        /**
         * Cada vez que se modifica la selección del componente, se cargan
         */
        componentSelectedFunction (newValue) {
            this.currentSubMenuEdit.route = newValue.route;
            this.currentSubMenuEdit.key = newValue.key;
            this.currentSubMenuEdit.keys = newValue.privileges;
            this.model.componentSelected = newValue;
        },
    },
    watch: {
        /**
         * Para validar el formulario
         * @param {*} newVal
         * @param {*} oldVal
         */
        isValidForm (newVal, oldVal) {
            if (newVal !== oldVal) {
                this.$emit('update:validForm', newVal)
            }
        },
        /**
         * Para restablecer las validaciones del formulario
         * @param {*} newVal
         */
        resetFormValidation (newVal) {
            if (newVal && this.$refs.enterpriseHeaderLogoForm) {
                this.$refs.enterpriseHeaderLogoForm.resetValidation()
            }
            if (newVal && this.$refs.menuValidForm) {
                this.$refs.menuValidForm.resetValidation()
            }
            if (newVal && this.$refs.subMenuValidForm) {
                this.$refs.subMenuValidForm.resetValidation()
            }
        },
        /**
         * Cuando se selecciona un componente en el selector de componentes, se modifican los valores del modelo del sub-menú
         * @param {*} newVal 
         */
        componentSelected (newVal) {
            if (newVal) {
                this.currentSubMenuEdit.key = newVal.key
                this.currentSubMenuEdit.route = newVal.route
                this.currentSubMenuEdit.keys = newVal.privileges
                this.validateComponentSelected(this.currentSubMenuEdit.id)
            } else {
                this.currentSubMenuEdit.key = null
                this.currentSubMenuEdit.route = ''
                this.currentSubMenuEdit.keys = []
                this.componentSelectedErrors = []
            }
        } 
    }
}
